import * as React from 'react';

import { assert } from '@gitbook/util-ts';

import type { EditorContainer } from './interfaces';

/**
 * Context to pass the EditorContainer down the tree.
 */
export const EditorContext = React.createContext<EditorContainer | null>(null);

/**
 * Hook to access the current editor.
 */
export function useEditor(): EditorContainer {
    const editor = React.useContext(EditorContext);
    assert(editor !== null, 'useEditor was used outside an <Editor />');
    return editor;
}

/**
 * Context to pass the editor focus down the tree.
 */
export const EditorFocusContext = React.createContext<boolean | null>(null);

/**
 * Hook to access the current editor focus.
 */
export function useIsEditorFocused() {
    const editor = React.useContext(EditorFocusContext);
    assert(editor !== null, 'useIsEditorFocused was used outside an <Editor />');
    return editor;
}

const listenCompositionEvent = (callback: () => void): (() => void) => {
    const notify = () => callback();
    document.addEventListener('compositionstart', notify);
    document.addEventListener('compositionend', notify);
    return () => {
        document.removeEventListener('compositionstart', notify);
        document.removeEventListener('compositionend', notify);
    };
};

export function useIsEditorComposing() {
    const editor = useEditor();
    return React.useSyncExternalStore(listenCompositionEvent, () => {
        return editor.isComposing;
    });
}
